import { useMemo, useEffect, useState } from 'react';
import './DownloadSection.css';
import pipelineService from '../../services/pipelineService';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { getDisplayFilename, formatFileSize, normalizeFileMetadata } from '../../utils/fileUtils';
import { warnLog, errorLog } from '../../utils/logger';

const isMainAsset = (filename = '') => {
  const lower = filename.toLowerCase();
  if (!lower) {
    return false;
  }
  if (lower.includes('preview')) {
    return false;
  }
  return (
    lower.endsWith('.obj') ||
    lower.endsWith('.mtl') ||
    lower.endsWith('.ply') ||
    (lower.endsWith('.png') && (lower.includes('texture') || lower.includes('material')))
  );
};

const getFileIcon = (filename = '') => {
  const ext = filename.toLowerCase().split('.').pop();
  switch (ext) {
    case 'obj':
      return '🎯';
    case 'mtl':
      return '🎨';
    case 'png':
    case 'jpg':
    case 'jpeg':
      return '🖼️';
    case 'npy':
      return '📊';
    case 'ply':
      return '📐';
    default:
      return '📄';
  }
};

const DownloadSection = ({ sessionId, sessionFiles, sessionInfo, loading = false }) => {
  const { isAuthenticated, user } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getAccessToken();
      if (!token || !isAuthenticated) {
        warnLog('Authentication required for downloads');
      }
      setAuthChecked(true);
    };

    checkAuth();
  }, [isAuthenticated, user]);

  const downloadAssets = useMemo(() => {
    if (!sessionId) {
      return { mainFiles: [], additionalFiles: [] };
    }

    const collected = [];

    if (sessionFiles?.files_by_step) {
      Object.values(sessionFiles.files_by_step).forEach((stepEntry) => {
        const consolidated = Array.isArray(stepEntry.consolidated_files)
          ? stepEntry.consolidated_files
          : [];
        const fallback = !consolidated.length && Array.isArray(stepEntry.fallback_files)
          ? stepEntry.fallback_files
          : [];

        const stepFiles = [...consolidated, ...fallback];

        stepFiles
          .map((file) => normalizeFileMetadata(file, stepEntry.step_type))
          .filter(Boolean)
          .forEach((file) => collected.push(file));
      });
    }

    if (sessionInfo?.steps) {
      sessionInfo.steps.forEach((step) => {
        if (!Array.isArray(step.files)) {
          return;
        }
        step.files.forEach((filename) => {
          if (typeof filename !== 'string') {
            return;
          }
          const exists = collected.some((file) => file.filename === filename);
          if (!exists) {
            const normalized = normalizeFileMetadata({ filename }, step.step_type);
            if (normalized) {
              collected.push(normalized);
            }
          }
        });
      });
    }

    // De-duplicate by step + canonical filename while preferring richer metadata entries
    let sequence = 0;
    const uniqueMap = new Map();

    const shouldReplace = (existingEntry, candidateEntry) => {
      if (!existingEntry) {
        return true;
      }

      const existingSize = existingEntry.file_size || 0;
      const candidateSize = candidateEntry.file_size || 0;

      if (!existingSize && candidateSize) {
        return true;
      }
      if (candidateSize && existingSize && candidateSize > existingSize) {
        return true;
      }
      if (candidateEntry.source === 'consolidated' && existingEntry.source !== 'consolidated') {
        return true;
      }

      return false;
    };

    collected.forEach((file) => {
      const canonical = file.canonical_name || (file.filename || '').toLowerCase();
      const key = canonical
        ? `${file.stepType || ''}|${canonical}`
        : `${file.stepType || ''}|__auto_${sequence}`;
      const existing = uniqueMap.get(key);

      if (!existing) {
        uniqueMap.set(key, { file, index: sequence });
      } else if (shouldReplace(existing.file, file)) {
        uniqueMap.set(key, { file, index: existing.index });
      }

      sequence += 1;
    });

    const uniqueFiles = Array.from(uniqueMap.values())
      .sort((a, b) => a.index - b.index)
      .map((entry) => entry.file);

    const mainFiles = [];
    const additionalFiles = [];

    uniqueFiles.forEach((file) => {
      if (isMainAsset(file.filename)) {
        mainFiles.push(file);
      } else {
        additionalFiles.push(file);
      }
    });

    return { mainFiles, additionalFiles };
  }, [sessionFiles, sessionInfo, sessionId]);

  const handleDownloadFile = async (file) => {
    try {
      const blob = await pipelineService.downloadFile(sessionId, file.stepType, file);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.display_name || getDisplayFilename(file.filename, file.stepType);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      errorLog('Error downloading file:', error);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please refresh the page and log in again.');
      } else {
        alert('Failed to download file. Please try again.');
      }
    }
  };

  if (!sessionId) {
    return null;
  }

  if (!authChecked || !isAuthenticated) {
    return (
      <div className="download-section">
        <div className="download-section__loading">
          <div className="loading-spinner" />
          <p>{!authChecked ? 'Checking authentication...' : 'Please log in to download files.'}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="download-section">
        <div className="download-section__loading">
          <div className="loading-spinner" />
          <p>Loading download information...</p>
        </div>
      </div>
    );
  }

  if (!sessionFiles && !sessionInfo) {
    return (
      <div className="download-section">
        <div className="download-empty">
          <p>No download information available yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="download-section">
      <div className="download-section__header">
        <h3>Download Results</h3>
      </div>

      {/* Optional physical size summary (millimetres), when available from backend */}
      {sessionInfo?.physical_size && Array.isArray(sessionInfo.physical_size.final_extent_mm) && (
        <div className="download-section__physical-size">
          {(() => {
            const [wx, wy, wz] = sessionInfo.physical_size.final_extent_mm;
            const width = wx != null ? Math.round(wx) : null;
            const height = wy != null ? Math.round(wy) : null;
            const depth = wz != null ? Math.round(wz) : null;
            const pieces = [];
            if (width !== null && !Number.isNaN(width)) {
              pieces.push(`${width}`);
            }
            if (height !== null && !Number.isNaN(height)) {
              pieces.push(`${height}`);
            }
            if (depth !== null && !Number.isNaN(depth)) {
              pieces.push(`${depth}`);
            }
            if (!pieces.length) {
              return null;
            }
            const unitLabel = sessionInfo.physical_size.units || 'mm';
            return (
              <span>
                Approx physical size:{' '}
                <strong>
                  {pieces.join(' × ')} {unitLabel}
                </strong>
              </span>
            );
          })()}
        </div>
      )}

      {downloadAssets.mainFiles.length > 0 && (
        <div className="download-category">
          <h4 className="download-category__title">Main Files</h4>
          <div className="download-files">
            {downloadAssets.mainFiles.map((file, index) => (
              <div key={`main-${index}`} className="download-file download-file--main">
                <div className="download-file__info">
                  <span className="download-file__icon">{getFileIcon(file.filename)}</span>
                  <div className="download-file__details">
                    <span className="download-file__name">
                      {file.display_name || getDisplayFilename(file.filename, file.stepType)}
                    </span>
                    <span className="download-file__meta">
                      {formatFileSize(file.file_size)} • {(file.stepType || '').replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="download-file__button"
                  onClick={() => handleDownloadFile(file)}
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {downloadAssets.additionalFiles.length > 0 && (
        <div className="download-category">
          <h4 className="download-category__title">Additional Files</h4>
          <div className="download-files download-files--compact">
            {downloadAssets.additionalFiles.map((file, index) => (
              <div key={`additional-${index}`} className="download-file download-file--compact">
                <div className="download-file__info">
                  <span className="download-file__icon">{getFileIcon(file.filename)}</span>
                  <div className="download-file__details">
                    <span className="download-file__name">
                      {file.display_name || getDisplayFilename(file.filename, file.stepType)}
                    </span>
                    <span className="download-file__meta">
                      {formatFileSize(file.file_size)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="download-file__button download-file__button--compact"
                  onClick={() => handleDownloadFile(file)}
                >
                  ⬇️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {downloadAssets.mainFiles.length === 0 && downloadAssets.additionalFiles.length === 0 && (
        <div className="download-empty">
          <p>No files available for download yet. Processing may still be in progress.</p>
        </div>
      )}
    </div>
  );
};

export default DownloadSection;
